import React, { useEffect, useRef, useState } from 'react';

interface IntersectionLazySectionProps {
  scrollProgress: number;
  startProgress: number;
  alpha: number;
  isHero?: boolean;
  children: React.ReactNode;
}

export const IntersectionLazySection: React.FC<IntersectionLazySectionProps> = React.memo(({
  scrollProgress,
  startProgress,
  alpha,
  isHero = false,
  children,
}) => {
  const [hasBeenNear, setHasBeenNear] = useState(isHero);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHero || hasBeenNear) return;

    // Direct progress threshold check (~300px equivalent of scroll height)
    if (scrollProgress >= startProgress - 0.025) {
      setHasBeenNear(true);
      return;
    }

    if (!sentinelRef.current || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasBeenNear(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '300px 0px 300px 0px', threshold: 0 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [scrollProgress, startProgress, isHero, hasBeenNear]);

  if (!isHero && !hasBeenNear && scrollProgress < startProgress - 0.025) {
    return <div ref={sentinelRef} style={{ display: 'none' }} />;
  }

  if (alpha <= 0.0001) return null;

  return <>{children}</>;
});
